import React from "react";
import { useTranslation } from "react-i18next";

// Icons
const LibraryIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
  </svg>
);
const UsersIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const FileTextIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
);

// Charts mock data
const mockAnalytics = {
  studentEngagement: [
    { name: "Jan", value: 65 },
    { name: "Feb", value: 72 },
    { name: "Mar", value: 85 },
    { name: "Apr", value: 81 },
    { name: "May", value: 75 },
    { name: "Jun", value: 90 },
  ],
  resourceUsage: [
    { name: "E-Books", value: 400, color: "#3b82f6" },
    { name: "Videos", value: 300, color: "#8b5cf6" },
    { name: "Articles", value: 300, color: "#10b981" },
    { name: "Simulations", value: 200, color: "#f97316" },
  ],
  classPerformance: {
    "Grade 9": 88,
    "Grade 10": 92,
    "Grade 11": 85,
    "Grade 12": 95,
  },
};

// Components
const StatCard = ({ title, value, icon, color }) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow flex items-center space-x-4">
    <div className={`rounded-full p-3 ${color}`}>{icon}</div>
    <div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const LineChart = ({ data }) => {
  const width = 500;
  const height = 280;
  const padding = 40;
  const maxValue = Math.max(...data.map((p) => p.value));
  const xScale = (width - 2 * padding) / (data.length - 1);
  const yScale = (height - 2 * padding) / maxValue;

  const points = data
    .map(
      (point, i) =>
        `${padding + i * xScale},${height - padding - point.value * yScale}`
    )
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      {data.map((point, i) => (
        <text
          key={i}
          x={padding + i * xScale}
          y={height - padding + 20}
          textAnchor="middle"
          fontSize="12"
          fill="#9ca3af"
        >
          {point.name}
        </text>
      ))}
      <polyline points={points} fill="none" stroke="#4f46e5" strokeWidth="3" />
      {data.map((point, i) => (
        <circle
          key={i}
          cx={padding + i * xScale}
          cy={height - padding - point.value * yScale}
          r="4"
          fill="#4f46e5"
        />
      ))}
    </svg>
  );
};

const PieChart = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulative = 0;

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <svg viewBox="0 0 64 64" className="w-40 h-40">
        {data.map((item, index) => {
          const startAngle = (cumulative / total) * 360;
          cumulative += item.value;
          const endAngle = (cumulative / total) * 360;
          const largeArcFlag = item.value / total > 0.5 ? 1 : 0;
          const x1 = 32 + 30 * Math.cos((Math.PI * startAngle) / 180);
          const y1 = 32 + 30 * Math.sin((Math.PI * startAngle) / 180);
          const x2 = 32 + 30 * Math.cos((Math.PI * endAngle) / 180);
          const y2 = 32 + 30 * Math.sin((Math.PI * endAngle) / 180);
          return (
            <path
              key={index}
              d={`M 32 32 L ${x1} ${y1} A 30 30 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
              fill={item.color}
            />
          );
        })}
      </svg>
      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2">
        {data.map((item) => (
          <div key={item.name} className="flex items-center text-sm">
            <span
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: item.color }}
            ></span>
            <span className="text-gray-600">{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarChart = ({ data }) => {
  const width = 500;
  const height = 300;
  const padding = 40;
  const maxValue = Math.max(...Object.values(data));
  const barWidth = (width - 2 * padding) / Object.keys(data).length;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      {Object.entries(data).map(([key, value], index) => {
        const barHeight = (value / maxValue) * (height - 2 * padding);
        return (
          <g key={key}>
            <rect
              x={padding + index * barWidth + barWidth * 0.1}
              y={height - padding - barHeight}
              width={barWidth * 0.8}
              height={barHeight}
              fill="#3b82f6"
              rx="4"
            />
            <text
              x={padding + index * barWidth + barWidth * 0.5}
              y={height - padding + 15}
              textAnchor="middle"
              fontSize="12"
              fill="#9ca3af"
            >
              {key}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const Analytics = () => {
  const { t } = useTranslation();

  return (
    <div className="p-6 sm:p-8 lg:p-10 bg-gray-50 min-h-screen space-y-8">
      <h2 className="text-2xl font-bold text-gray-800">
        {t("analytics.title")}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t("analytics.totalStudents")}
          value="1,245"
          icon={<UsersIcon className="w-6 h-6 text-white" />}
          color="bg-blue-500"
        />
        <StatCard
          title={t("analytics.staffMembers")}
          value="82"
          icon={<UsersIcon className="w-6 h-6 text-white" />}
          color="bg-purple-500"
        />
        <StatCard
          title={t("analytics.activeCourses")}
          value="112"
          icon={<LibraryIcon className="w-6 h-6 text-white" />}
          color="bg-green-500"
        />
        <StatCard
          title={t("analytics.resourcesAvailable")}
          value="5,678"
          icon={<FileTextIcon className="w-6 h-6 text-white" />}
          color="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <h3 className="font-bold text-lg mb-4 text-gray-700">
            {t("analytics.classPerformance")}
          </h3>
          <div className="h-80">
            <BarChart data={mockAnalytics.classPerformance} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <h3 className="font-bold text-lg mb-4 text-gray-700">
            {t("analytics.resourceUsage")}
          </h3>
          <div className="h-80">
            <PieChart data={mockAnalytics.resourceUsage} />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-sm">
        <h3 className="font-bold text-lg mb-4 text-gray-700">
          {t("analytics.studentEngagement")}
        </h3>
        <div className="h-80">
          <LineChart data={mockAnalytics.studentEngagement} />
        </div>
      </div>
    </div>
  );
};

export default Analytics;
